import type { Filter, FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';

type PriceRangeLabelOptions = {
  t?: TranslationFunction;
  localization?: Localization;
  unbounded?: boolean;
};

const NO_LIMIT_KEY = 'vacation.oss.components.searchfilters.labels.nolimit';

const isUnpriceable = (formatted: string) => Number.isNaN(parseFloat(formatted.replace(/[^\d.]/g, '')));

const maxLabel = (
  data: FilterDataRange,
  max: number,
  formatted: string,
  t: TranslationFunction,
  unbounded: boolean
) => {
  if (!unbounded) return formatted;

  const atSliderTop = max === data.maxValue;

  if (atSliderTop && !data.dynamicValues) return t(NO_LIMIT_KEY);
  if (isUnpriceable(formatted)) return t(NO_LIMIT_KEY);
  return atSliderTop ? `${formatted}+` : formatted;
};

export const priceRangeLabel = (
  filter: Filter,
  { t, localization, unbounded = false }: PriceRangeLabelOptions
): string => {
  const data = filter.data as FilterDataRange;
  const min = Math.floor(data.value?.min ?? data.default?.min ?? 0);
  const max = Math.floor(data.value?.max ?? data.default?.max ?? 0);

  if (!t || !localization) return `${min} - ${max}`;

  const minPrice = formatPrice({ price: min, localization });
  const maxPrice = maxLabel(data, max, formatPrice({ price: max, localization }), t, unbounded);

  return min > 0 ? `${minPrice} - ${maxPrice}` : t('vacation.oss.filters.budget.up_to', { price: maxPrice });
};
